import { ArrowRightLeft, Bot, Gift, Link2, RefreshCw } from "lucide-react"
import toast from "react-hot-toast"
import { ROBIN_CONNECT_MUTATION, robinConnectUrl } from "~/api/robin"
import { useCurrentUser } from "~/auth/CurrentUserContext"
import { useSafeMutation } from "~/common/useSafeMutation"
import { displayErrors } from "~/common/validations"
import { Button } from "~/shadcn/ui/button"
import { ModalDialog } from "~/ui/ModalDialog"
import { H2 } from "~/ui/typography"

import ZanoLogo from "~/images/zano_logo_full.svg?react"
import robinWordmark from "~/images/integrations/robin-wordmark"

const steps = [
  {
    icon: Link2,
    title: "You Connect",
    body: "Click Connect and Zano sends Robin your media kit URL.",
  },
  {
    icon: RefreshCw,
    title: "Always in sync",
    body: "Robin continuously reads your live Zano media kit. Any updates you make in Zano are automatically reflected in Robin.",
  },
  {
    icon: Bot,
    title: "Robin works for you",
    body: "Robin gets your roster, live performance data, audience demographics, generic data and then qualifies leads and negotiates brand deals with that information.",
  },
]

export const ConnectRobinModal = ({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) => {
  const currentUser = useCurrentUser()
  const [runRobinConnect, { loading }] = useSafeMutation(ROBIN_CONNECT_MUTATION)

  const onConnect = async () => {
    const agencySlug = currentUser.agency?.slug
    if (!agencySlug) return

    // Open Robin synchronously (before any await) so the browser treats it as a
    // user-initiated navigation and does not block the popup.
    window.open(robinConnectUrl(agencySlug), "_blank", "noopener,noreferrer")

    const { errors } = await runRobinConnect({ variables: { input: {} } })
    if (errors) {
      displayErrors(errors)
      return
    }

    toast("Finish connecting in the Robin tab")
    onClose()
  }

  return (
    <ModalDialog
      open={open}
      onClose={onClose}
      size="md"
      className="sm:max-w-[742px]"
    >
      <div className="flex flex-col items-center text-center px-2 pb-2">
        <div className="flex items-center justify-center gap-4 mb-6">
          <ZanoLogo className="w-24" />
          <ArrowRightLeft className="w-6 h-6 text-gray50" aria-hidden="true" />
          <img {...robinWordmark} alt="Robin" className="h-6 w-auto" />
        </div>

        <H2 className="mb-3">Connect Robin to your Zano roster</H2>
        <p className="text-gray50 max-w-xl mb-6">
          Robin is your AI agent that qualifies leads and negotiates brand deals
          on your behalf so no more deals fall through the cracks.
        </p>

        <div className="w-full border-t border-gray30 mb-6" />

        <h3 className="font-bold text-sm tracking-wide text-gray50 mb-6">
          HOW IT WORKS
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-0 sm:divide-x sm:divide-gray30 mb-8 text-left">
          {steps.map(({ icon: Icon, title, body }) => (
            <div key={title} className="flex flex-col sm:px-5">
              <div className="w-12 h-12 rounded-full bg-gray20 flex items-center justify-center mb-3">
                <Icon className="w-5 h-5 text-gray90" />
              </div>
              <p className="font-bold mb-2">{title}</p>
              <p className="text-gray50 text-sm">{body}</p>
            </div>
          ))}
        </div>

        <div className="flex items-start gap-3 w-full rounded-lg border border-gray30 bg-gray20/30 p-4 mb-8 text-left">
          <Gift className="w-6 h-6 text-gray50 shrink-0" />
          <div className="text-sm text-gray90">
            <p className="font-bold mb-1">New to Robin?</p>
            <p>
              Don&apos;t have a Robin account yet? Click Connect and we&apos;ll
              guide you to create one. As a Zano user, you&apos;ll get{" "}
              <span className="font-bold text-greenDark">14 days free</span> to
              try Robin.
            </p>
          </div>
        </div>

        <Button
          className="w-full"
          size="lg"
          onClick={onConnect}
          disabled={loading}
        >
          Connect to Robin
        </Button>
      </div>
    </ModalDialog>
  )
}
